import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, Checkbox, Input, Select } from '@/components/ui';
import type { IssueSearch, KeywordType, TaxInvoiceSearch, TaxType } from '../api/taxInvoiceApi';
import { KEYWORD_TYPES, ORDER_STATUS_FILTER, TAX_TYPES, daysAgoKst, todayKst } from '../lib/taxInvoiceLib';

/** 발행 요청·발행 내역 공용 검색(고도몰 검색 영역): 검색어·사업자번호·기간·주문상태·과세구분(+내역: 기간 기준·취소 포함). */

export const defaultRequestSearch = (): TaxInvoiceSearch => ({
  keywordType: 'ALL',
  keyword: '',
  businessNo: '',
  from: daysAgoKst(30),
  to: todayKst(),
  orderStatuses: [],
  taxType: 'ALL',
});

export const defaultIssueSearch = (): IssueSearch => ({
  ...defaultRequestSearch(),
  dateType: 'ISSUE_DATE',
  includeCanceled: false,
});

export const useTaxInvoiceLabels = () => {
  const { t } = useTranslation('orders');
  return {
    t,
    keywordType: (k: string) =>
      ({
        ALL: t('orders.taxInvoice.keywordType.ALL', '통합검색'),
        COMPANY: t('orders.taxInvoice.keywordType.COMPANY', '회사명'),
        CEO: t('orders.taxInvoice.keywordType.CEO', '대표자'),
        REQUESTER: t('orders.taxInvoice.keywordType.REQUESTER', '요청인(주문자)'),
        ORDER_NO: t('orders.taxInvoice.keywordType.ORDER_NO', '주문번호'),
      })[k] ?? k,
    taxType: (k: string) =>
      ({
        ALL: t('orders.taxInvoice.taxType.ALL', '전체'),
        TAXABLE: t('orders.taxInvoice.taxType.TAXABLE', '과세'),
        FREE: t('orders.taxInvoice.taxType.FREE', '면세'),
        MIXED: t('orders.taxInvoice.taxType.MIXED', '복합과세'),
      })[k] ?? k,
    invoiceKind: (k: string) =>
      ({ TAX_INVOICE: t('orders.taxInvoice.kind.TAX_INVOICE', '세금계산서'), INVOICE: t('orders.taxInvoice.kind.INVOICE', '계산서') })[k] ?? k,
    issueType: (k: string) =>
      ({ GENERAL: t('orders.taxInvoice.issueType.GENERAL', '일반'), ELECTRONIC: t('orders.taxInvoice.issueType.ELECTRONIC', '전자') })[k] ?? k,
    issueStatus: (k: string) =>
      ({ ISSUED: t('orders.taxInvoice.issueStatus.ISSUED', '발행'), CANCELED: t('orders.taxInvoice.issueStatus.CANCELED', '발행 취소') })[k] ?? k,
    orderStatus: (k: string) => t(`orders.orderStatus.${k}`, k),
  };
};

interface Props<T extends TaxInvoiceSearch> {
  initial: T;
  onSearch: (q: T) => void;
  /** 발행 내역: 기간 기준(발행일/요청일)과 취소 포함. */
  issueMode?: boolean;
  makeDefault: () => T;
}

export const TaxInvoiceSearchForm = <T extends TaxInvoiceSearch>({ initial, onSearch, issueMode, makeDefault }: Props<T>) => {
  const L = useTaxInvoiceLabels();
  const { t } = L;
  const [q, setQ] = useState<T>(initial);
  const patch = (p: Partial<T>) => setQ((prev) => ({ ...prev, ...p }));
  const iq = q as unknown as IssueSearch;

  const toggleStatus = (st: string, on: boolean) =>
    patch({ orderStatuses: on ? [...q.orderStatuses, st] : q.orderStatuses.filter((x) => x !== st) } as Partial<T>);

  return (
    <form
      className="flex flex-col gap-3 rounded-lg p-4 text-sm"
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
      onSubmit={(e) => {
        e.preventDefault();
        onSearch(q);
      }}
      data-testid="tax-invoice-search"
    >
      <div className="grid grid-cols-[100px_1fr] items-center gap-x-3 gap-y-3">
        <span className="text-[color:var(--content-muted)]">{t('orders.taxInvoice.search.keyword', '검색어')}</span>
        <div className="flex flex-wrap gap-2">
          <Select
            className="w-40"
            value={q.keywordType}
            onChange={(e) => patch({ keywordType: e.target.value as KeywordType } as Partial<T>)}
            aria-label={t('orders.taxInvoice.search.keywordType', '검색 항목')}
          >
            {KEYWORD_TYPES.map((k) => (
              <option key={k} value={k}>
                {L.keywordType(k)}
              </option>
            ))}
          </Select>
          <Input
            className="max-w-xs"
            value={q.keyword}
            onChange={(e) => patch({ keyword: e.target.value } as Partial<T>)}
            aria-label={t('orders.taxInvoice.search.keyword', '검색어')}
            data-testid="tax-invoice-keyword"
          />
        </div>

        <span className="text-[color:var(--content-muted)]">{t('orders.taxInvoice.search.businessNo', '사업자번호')}</span>
        <Input
          className="max-w-xs"
          value={q.businessNo}
          onChange={(e) => patch({ businessNo: e.target.value } as Partial<T>)}
          placeholder="000-00-00000"
          aria-label={t('orders.taxInvoice.search.businessNo', '사업자번호')}
        />

        <span className="text-[color:var(--content-muted)]">{t('orders.taxInvoice.search.period', '기간')}</span>
        <div className="flex flex-wrap items-center gap-2">
          {issueMode ? (
            <Select
              className="w-32"
              value={iq.dateType}
              onChange={(e) => patch({ dateType: e.target.value } as unknown as Partial<T>)}
              aria-label={t('orders.taxInvoice.search.dateType', '기간 기준')}
            >
              <option value="ISSUE_DATE">{t('orders.taxInvoice.search.issueDate', '발행일')}</option>
              <option value="REQUEST_DATE">{t('orders.taxInvoice.search.requestDate', '발행요청일')}</option>
            </Select>
          ) : (
            <span className="text-[color:var(--content-muted)]">{t('orders.taxInvoice.search.requestDate', '발행요청일')}</span>
          )}
          <Input type="date" className="w-40" value={q.from} onChange={(e) => patch({ from: e.target.value } as Partial<T>)} aria-label={t('orders.taxInvoice.search.from', '시작일')} />
          <span>~</span>
          <Input type="date" className="w-40" value={q.to} onChange={(e) => patch({ to: e.target.value } as Partial<T>)} aria-label={t('orders.taxInvoice.search.to', '종료일')} />
          {[7, 30, 90].map((d) => (
            <Button key={d} type="button" size="sm" variant="ghost" onClick={() => patch({ from: daysAgoKst(d), to: todayKst() } as Partial<T>)}>
              {t('orders.taxInvoice.search.days', { defaultValue: '{{n}}일', n: d })}
            </Button>
          ))}
        </div>

        <span className="text-[color:var(--content-muted)]">{t('orders.taxInvoice.search.orderStatus', '주문상태')}</span>
        <div className="flex flex-wrap gap-x-4 gap-y-1" data-testid="tax-invoice-order-status">
          {ORDER_STATUS_FILTER.map((st) => (
            <label key={st} className="inline-flex items-center gap-1.5">
              <Checkbox checked={q.orderStatuses.includes(st)} onCheckedChange={(v) => toggleStatus(st, v === true)} aria-label={L.orderStatus(st)} />
              {L.orderStatus(st)}
            </label>
          ))}
        </div>

        <span className="text-[color:var(--content-muted)]">{t('orders.taxInvoice.search.taxType', '과세구분')}</span>
        <div className="flex flex-wrap items-center gap-4">
          {TAX_TYPES.map((k) => (
            <label key={k} className="inline-flex items-center gap-1.5">
              <input type="radio" name={issueMode ? 'issue-tax-type' : 'request-tax-type'} checked={q.taxType === k} onChange={() => patch({ taxType: k as TaxType } as Partial<T>)} />
              {L.taxType(k)}
            </label>
          ))}
          {issueMode ? (
            <label className="inline-flex items-center gap-1.5">
              <Checkbox checked={iq.includeCanceled} onCheckedChange={(v) => patch({ includeCanceled: v === true } as unknown as Partial<T>)} />
              {t('orders.taxInvoice.search.includeCanceled', '발행 취소 포함')}
            </label>
          ) : null}
        </div>
      </div>
      <div className="flex justify-center gap-2">
        <Button type="submit" size="sm" variant="primary" data-testid="tax-invoice-search-submit">
          {t('orders.taxInvoice.search.submit', '검색')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            const d = makeDefault();
            setQ(d);
            onSearch(d);
          }}
        >
          {t('orders.taxInvoice.search.reset', '초기화')}
        </Button>
      </div>
    </form>
  );
};
